import React, { useState } from "react";
import { Link } from "react-router-dom";
import RulemakingTasks from "./RulemakingTasks";

function formatDate(value) {
    return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// The soonest thing a reader can act on, shown on the collapsed row.
function nextStep(bill) {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    if (bill.next_notice_event) {
        const notice = bill.notices.find(n => n.next_event === bill.next_notice_event);
        const isHearing = notice?.hearing_date && new Date(notice.hearing_date).getTime() === new Date(bill.next_notice_event).getTime();
        return { tone: "due", text: `${isHearing ? "Hearing" : "Comments due"} ${formatDate(bill.next_notice_event)}` };
    }
    if (bill.next_deadline && !bill.deadline_passed) return { tone: "due", text: `Deadline ${formatDate(bill.next_deadline)}` };
    if (bill.deadline_passed) return { tone: "late", text: "Deadline passed" };
    return null;
}

function Agency({ name, organizationId }) {
    return organizationId ? <Link to={`/organization/${organizationId}`}>{name}</Link> : <>{name}</>;
}

// Enacted bills the Workspace tracks that still face rulemaking, laid out like the Bill Work list.
// Every bill starts open; `closedIds` holds the ones the reader has collapsed.
// `tasking` ({ teams, tasks, userId, onCreated, onUpdated, onDeleted }) adds a task panel to each notice.
export default function WorkspaceRulemakingList({ bills, sectionLabel, sectionLabelSingular, tasking }) {
    const [closedIds, setClosedIds] = useState(new Set());
    const [clientFilter, setClientFilter] = useState("");

    const clientOptions = [...new Map(bills.flatMap(b => b.clients).map(c => [c.client_id, c])).values()]
        .sort((a, b) => a.client_name.localeCompare(b.client_name));
    const visibleBills = bills.filter(bill => !clientFilter
        || (clientFilter === "__none__"
            ? bill.clients.length === 0
            : bill.clients.some(c => String(c.client_id) === clientFilter)));
    const allOpen = visibleBills.every(b => !closedIds.has(b.bill_id));

    const toggleBill = id => setClosedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleAll = () => setClosedIds(allOpen ? new Set(visibleBills.map(b => b.bill_id)) : new Set());

    return (
        <>
            <div className="tw-bill-work-toolbar mb-3">
                {clientOptions.length > 0 && (
                    <div className="tw-bill-filter-bar">
                        <label className="tw-section-label me-2">{sectionLabelSingular.toLowerCase()}:</label>
                        <select className="form-select form-select-sm tw-bulk-select" value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
                            <option value="">All bills</option>
                            <option value="__none__">No {sectionLabel.toLowerCase()} assigned</option>
                            {clientOptions.map(c => (
                                <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
                            ))}
                        </select>
                        <button
                            className={`tw-filter-clear-btn${clientFilter ? "" : " invisible"}`}
                            onClick={() => setClientFilter("")}
                            tabIndex={clientFilter ? 0 : -1}
                            title="Clear filter"
                        >
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>
                )}
                <div className="tw-bill-work-actions">
                    <button className="tw-reassign-trigger" onClick={toggleAll} disabled={visibleBills.length === 0}>
                        <i className={`bi ${allOpen ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`} />{allOpen ? "Collapse all" : "Expand all"}
                    </button>
                </div>
            </div>

            {visibleBills.length === 0 && (
                <p className="text-muted small fst-italic">No bills match this filter.</p>
            )}

            <div className="tw-bill-list">
                {visibleBills.map(bill => {
                    const isExpanded = !closedIds.has(bill.bill_id);
                    const step = nextStep(bill);
                    const hasNotices = bill.notices.length > 0;
                    const hasAdopted = bill.adopted_actions?.length > 0;
                    const hasEvents = bill.upcoming_events?.length > 0;
                    return (
                        <div key={bill.bill_id} className="tw-bill-row">
                            <div className="tw-bill-header" onClick={() => toggleBill(bill.bill_id)}>
                                <Link className="tw-bill-id" to={`/legislation/bill/${bill.bill_id}`} onClick={e => e.stopPropagation()}>{bill.display}</Link>
                                <span className="tw-bill-title">{bill.title}</span>
                                <div className="tw-bill-chips">
                                    {step && <span className={`tw-rule-chip tw-rule-chip--${step.tone}`}>{step.text}</span>}
                                    {bill.notice_count > 0 && <span className="tw-role-chip tw-role-chip--monitor">{bill.notice_count} {bill.notice_count === 1 ? "notice" : "notices"}</span>}
                                    {bill.team_position && <span className="tw-role-chip tw-role-chip--lead" title="Workspace position">{bill.team_position}</span>}
                                    {bill.clients.map(c => (
                                        <span key={c.client_id} className="tw-client-chip" title={c.position ? `${c.client_name}: ${c.position}` : c.client_name}>
                                            {c.client_name}{c.position ? ` · ${c.position}` : ""}
                                        </span>
                                    ))}
                                </div>
                                <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} tw-expand-icon`} />
                            </div>

                            {isExpanded && (
                                <div className="tw-bill-sections">
                                    <div className="tw-bill-section">
                                        <div className="tw-bill-section-title"><i className="bi bi-calendar-event me-1" />Timeline</div>
                                        <div className="tw-rule-facts">
                                            <span>{bill.chapter || "Chaptered"} · signed {formatDate(bill.chaptered_at)}</span>
                                            <span>Takes effect ~{formatDate(bill.effective_date)}</span>
                                            {bill.next_deadline && (
                                                <span className={bill.deadline_passed ? "tw-rule-late" : "tw-rule-due"}>
                                                    {bill.deadline_passed ? "Statutory deadline passed" : "Statutory deadline"} {formatDate(bill.next_deadline)}
                                                </span>
                                            )}
                                        </div>
                                        {bill.agencies.length > 0 && (
                                            <div className="tw-rule-agencies">
                                                {bill.agencies.map(a => (
                                                    <span key={a.key} className="tw-rule-agency">
                                                        <Agency name={a.label} organizationId={a.kind === "agency" ? a.organization_id : null} />
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {bill.duties.length > 0 && (
                                        <div className="tw-bill-section">
                                            <div className="tw-bill-section-title"><i className="bi bi-file-earmark-text me-1" />What the bill requires</div>
                                            {bill.duties.map((d, i) => (
                                                <p className="tw-rule-duty" key={i}>
                                                    <span className="tw-role-chip tw-role-chip--monitor me-1">{d.kind === "regulations" ? "Regulations" : "Guidance"}</span>
                                                    {d.sentence}
                                                </p>
                                            ))}
                                        </div>
                                    )}

                                    {hasNotices && (
                                        <div className="tw-bill-section">
                                            <div className="tw-bill-section-title"><i className="bi bi-megaphone me-1" />Proposed regulations</div>
                                            {bill.notices.map(n => (
                                                <div className="tw-rule-item" key={n.file_number}>
                                                    <div className="tw-rule-item-head">
                                                        <span className="tw-rule-item-agency"><Agency name={n.agency} organizationId={n.agency_organization_id} /></span>
                                                        <span className="tw-rule-item-subject">{n.subject}</span>
                                                    </div>
                                                    <div className="tw-rule-facts">
                                                        {n.hearing_status === "scheduled" && n.hearing_date && (
                                                            <span className="tw-rule-due">
                                                                Hearing {formatDate(n.hearing_date)}{n.hearing_time ? `, ${n.hearing_time}` : ""}{n.hearing_location ? ` · ${n.hearing_location}` : ""}
                                                            </span>
                                                        )}
                                                        {n.hearing_status === "none" && <span>No hearing scheduled (one can be requested)</span>}
                                                        {n.comment_deadline && <span>Comments due {formatDate(n.comment_deadline)}</span>}
                                                        <span>Noticed {formatDate(n.register_date)}</span>
                                                        {n.adopted && (
                                                            <span className="tw-rule-adopted">
                                                                <i className="bi bi-check-circle-fill me-1" />Adopted: filed {formatDate(n.adopted.filed_date)}{n.adopted.effective_date ? `, effective ${formatDate(n.adopted.effective_date)}` : ""}
                                                            </span>
                                                        )}
                                                        {n.source_url && <a href={n.source_url} target="_blank" rel="noopener noreferrer">Notice {n.file_number}</a>}
                                                    </div>
                                                    <div className="tw-rule-match">
                                                        {n.match === "chapter" && "Cites this bill's chapter"}
                                                        {n.match === "bill" && "Names this bill"}
                                                        {n.match === "sections" && `Possibly related: cites ${n.shared_sections.join(", ")}`}
                                                    </div>
                                                    {tasking && (
                                                        <RulemakingTasks
                                                            item={{
                                                                ref: `notice:${n.file_number}`,
                                                                title: `${n.agency}: ${n.subject}`,
                                                                url: n.source_url,
                                                                dueDate: [n.hearing_status === "scheduled" ? n.hearing_date : null, n.comment_deadline].filter(Boolean).sort()[0],
                                                            }}
                                                            {...tasking}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                            {bill.notice_count > bill.notices.length && (
                                                <p className="tw-rule-match mb-0">{bill.notice_count - bill.notices.length} more possibly related notice(s) not shown</p>
                                            )}
                                        </div>
                                    )}

                                    {hasAdopted && (
                                        <div className="tw-bill-section">
                                            <div className="tw-bill-section-title"><i className="bi bi-check2-circle me-1" />Adopted regulations</div>
                                            {bill.adopted_actions.map(a => (
                                                <div className="tw-rule-item" key={a.oal_file_number}>
                                                    <div className="tw-rule-item-head">
                                                        <span className="tw-rule-item-agency"><Agency name={a.agency} organizationId={a.agency_organization_id} /></span>
                                                        <span className="tw-rule-item-subject">{a.subject}</span>
                                                    </div>
                                                    <div className="tw-rule-facts">
                                                        <span className="tw-rule-adopted">
                                                            <i className="bi bi-check-circle-fill me-1" />Adopted: filed {formatDate(a.filed_date)}{a.effective_date ? `, effective ${formatDate(a.effective_date)}` : ""}
                                                        </span>
                                                        {a.source_url && <a href={a.source_url} target="_blank" rel="noopener noreferrer">OAL file {a.oal_file_number}</a>}
                                                    </div>
                                                    <div className="tw-rule-match">Filing summary names this bill</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {hasEvents && (
                                        <div className="tw-bill-section tw-bill-section--last">
                                            <div className="tw-bill-section-title"><i className="bi bi-building me-1" />Coming up at the agencies named in this bill</div>
                                            {bill.upcoming_events.map(e => (
                                                <div className="tw-rule-facts" key={`${e.agency}-${e.type}-${e.date}-${e.title}`}>
                                                    <span className="tw-rule-due">{formatDate(e.date)}{e.time ? `, ${e.time}` : ""}</span>
                                                    <span>{e.agency}: {e.type === "hearing" ? `Public hearing on ${e.title}` : e.title}</span>
                                                    {e.location && <span>{e.location}</span>}
                                                    {e.url && <a href={e.url} target="_blank" rel="noopener noreferrer">{e.type === "hearing" ? "Notice" : "Agenda"}</a>}
                                                </div>
                                            ))}
                                            <div className="tw-rule-match">Calendar items for the whole body; not necessarily about this bill</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
}
